import { Box, Checkbox, Divider, FormControlLabel, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog, { type DialogCheckedState } from "../../../../../../components/UIToolkit/Popups/Dialog";

/** Active employee eligible for ignore-from-schedule when a role string is marked Don't Sync (DAT-154). */
export interface DontSyncEmployee {
	nurseId: number;
	name: string;
	actualShiftsLast30: number;
	scheduledShiftsLast30: number;
}

interface DontSyncConfirmDialogProps {
	open: boolean;
	roleString: string;
	/** Active employees currently synced under this role string. */
	employees: DontSyncEmployee[];
	/** Nurse ids to set ignore-from-schedule ([] = stop sync, keep everyone scheduled). */
	onConfirm: (deactivateNurseIds: number[]) => void;
	onClose: () => void;
}

/**
 * Confirmation shown when an admin marks a role string Don't Sync while it still has active employees
 * (DAT-154). Sync stops for everyone going forward; checked employees are also set to ignore-from-schedule
 * now (with 30-day shift counts shown). Confirming with nothing checked is the safe path — stop sync only.
 */
export const DontSyncConfirmDialog: FC<DontSyncConfirmDialogProps> = ({ open, roleString, employees, onConfirm, onClose }) => {
	const { t } = useTranslation();
	// Checked nurse ids = employees to set ignore-from-schedule. The parent keeps one mounted instance and
	// drives visibility via `open`, so clear the selection each time the dialog (re)opens for a role string.
	const [selected, setSelected] = useState<Set<number>>(() => new Set());
	useEffect(() => {
		if (open) setSelected(new Set());
	}, [open]);

	const allSelected = employees.length > 0 && selected.size === employees.length;
	const someSelected = selected.size > 0 && !allSelected;

	const toggle = (nurseId: number) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(nurseId)) next.delete(nurseId);
			else next.add(nurseId);
			return next;
		});

	const toggleAll = () => setSelected(allSelected ? new Set() : new Set(employees.map((e) => e.nurseId)));

	const hasSelection = selected.size > 0;

	// Required acknowledgement, shown only when at least one employee is selected. Keyed on `hasSelection`
	// so toggling individuals doesn't re-seed it.
	const checkboxes = useMemo(
		() => (hasSelection ? [{ label: t("hris.mapping.dontSyncConfirm.deactivateAck"), required: true, defaultValue: false }] : []),
		[hasSelection, t],
	);

	const handleConfirm = (_checkedState?: DialogCheckedState[]) => {
		onConfirm(Array.from(selected));
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.dontSyncConfirm.title", { roleString })}
			dataTestId="hris-mapping-dont-sync-confirm-dialog"
			confirm={t("hris.mapping.dontSyncConfirm.confirm")}
			onConfirm={handleConfirm}
			checkboxes={checkboxes}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, minWidth: 380 }}>
					<Typography variant="body2" color="text.secondary">
						{t("hris.mapping.dontSyncConfirm.description", { count: employees.length })}
					</Typography>
					{/* DAT-435: the pane may show employees from sibling departments sharing this HRIS file — clarify the edit's scope. */}
					<Typography variant="caption" color="text.secondary">
						{t("hris.mapping.scopeNote")}
					</Typography>
					<FormControlLabel
						control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} data-testid="hris-mapping-dont-sync-select-all" />}
						label={t("hris.mapping.dontSyncConfirm.selectAll")}
					/>
					<Divider />
					<Box sx={{ display: "flex", flexDirection: "column", maxHeight: 280, overflow: "auto" }} data-testid="hris-mapping-dont-sync-employee-list">
						{employees.map((emp) => (
							<FormControlLabel
								key={emp.nurseId}
								control={
									<Checkbox
										checked={selected.has(emp.nurseId)}
										onChange={() => toggle(emp.nurseId)}
										data-testid={`hris-mapping-dont-sync-employee-${emp.nurseId}`}
									/>
								}
								label={
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										<Typography variant="body2">{emp.name}</Typography>
										<Typography variant="caption" color="text.secondary">
											{t("hris.mapping.dontSyncConfirm.shiftCounts", {
												actual: emp.actualShiftsLast30,
												scheduled: emp.scheduledShiftsLast30,
											})}
										</Typography>
									</Box>
								}
							/>
						))}
					</Box>
				</Box>
			}
		/>
	);
};
